import { z } from 'zod'
import { AI_MODELS, ALERT_TYPES, CHECK_INTERVALS, TIERS } from '../types/enums.ts'
import { ModelSchema, TierSchema } from './db.ts'

/** POST /api/waitlist */
export const WaitlistRequestSchema = z.object({
  email: z.string().email({ message: 'Invalid email address' }),
  businessName: z.string().min(1).max(200),
})
export type WaitlistRequest = z.infer<typeof WaitlistRequestSchema>

/** POST /api/contact */
export const ContactRequestSchema = z.object({
  name: z.string().min(1).max(200),
  email: z.string().email({ message: 'Invalid email address' }),
  subject: z.enum(['general', 'pricing', 'enterprise', 'bug', 'partnership']),
  message: z.string().min(1).max(5000),
  turnstileToken: z.string().min(1).optional(),
})
export type ContactRequest = z.infer<typeof ContactRequestSchema>

/** POST /api/auth/register */
export const RegisterRequestSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8).max(128),
  turnstileToken: z.string().min(1).optional(),
})
export type RegisterRequest = z.infer<typeof RegisterRequestSchema>

/** POST /api/auth/login */
export const LoginRequestSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8),
  turnstileToken: z.string().min(1).optional(),
})
export type LoginRequest = z.infer<typeof LoginRequestSchema>

/** Auth response (register + login) */
export const AuthResponseSchema = z.object({
  token: z.string(),
  user: z.object({
    id: z.string(),
    email: z.string().email(),
    tier: TierSchema,
    trialExpiresAt: z.number().nullable(),
  }),
})
export type AuthResponse = z.infer<typeof AuthResponseSchema>

/** POST /api/onboard */
export const OnboardRequestSchema = z.object({
  brandName: z.string().min(1).max(200),
  brandUrl: z.preprocess(
    (v) => {
      if (!v || typeof v !== 'string' || !v.trim()) return undefined
      const s = v.trim()
      return /^https?:\/\//i.test(s) ? s : `https://${s}`
    },
    z.string().url().optional(),
  ),
  industry: z.string().min(1).max(100),
  prompts: z.array(z.string().min(10).max(500)).min(1).max(100),
  models: z.array(z.enum(AI_MODELS)).min(1),
  checkInterval: z.enum(CHECK_INTERVALS),
  alertType: z.enum(ALERT_TYPES).optional(),
  alertDestination: z.string().optional(),
})
export type OnboardRequest = z.infer<typeof OnboardRequestSchema>

/** GET /api/dashboard */
export const TrendPointSchema = z.object({
  date: z.string(),
  mentions: z.number(),
  model: ModelSchema,
})

export const TrendWidePointSchema = z.object({
  date: z.string(),
  perModel: z.record(z.string(), z.number()),
})
export type TrendWidePoint = z.infer<typeof TrendWidePointSchema>

export const StatsPeriodSchema = z.object({
  totalChecks: z.number(),
  totalMentions: z.number(),
  mentionRate: z.number(),
  avgRankWhenMentioned: z.number().nullable(),
  sentiment: z.object({
    positive: z.number(),
    neutral: z.number(),
    negative: z.number(),
    unscored: z.number(),
  }),
})
export type StatsPeriod = z.infer<typeof StatsPeriodSchema>

export const BrandStatsSchema = z.object({
  current: StatsPeriodSchema,
  previous: StatsPeriodSchema.nullable(),
  deltas: z.object({
    mentionRatePct: z.number(),
    avgRankDelta: z.number().nullable(),
  }).nullable(),
})
export type BrandStats = z.infer<typeof BrandStatsSchema>

export const BrandSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  stats: BrandStatsSchema,
  modelsConfigured: z.number(),
  modelsCovering: z.number(),
  lastCheckedAt: z.number().nullable(),
  trendWide: z.array(TrendWidePointSchema),
})

export const DashboardResponseSchema = z.object({
  brands: z.array(BrandSummarySchema),
  user: z.object({
    tier: TierSchema,
    creditsBalance: z.number(),
    trialExpiresAt: z.number().nullable(),
  }),
})
export type DashboardResponse = z.infer<typeof DashboardResponseSchema>

/** GET /api/insights/:brandId */
export const PerModelStatSchema = z.object({
  model: ModelSchema,
  checks: z.number(),
  mentions: z.number(),
  mentionRate: z.number(),
  avgRank: z.number().nullable(),
})
export type PerModelStat = z.infer<typeof PerModelStatSchema>

export const PerPromptStatSchema = z.object({
  promptId: z.string(),
  promptText: z.string(),
  checks: z.number(),
  mentions: z.number(),
  mentionRate: z.number(),
})
export type PerPromptStat = z.infer<typeof PerPromptStatSchema>

export const TopCompetitorSchema = z.object({
  name: z.string(),
  normalizedName: z.string(),
  mentionCount: z.number(),
  sharePct: z.number(),
})
export type TopCompetitor = z.infer<typeof TopCompetitorSchema>

export const SentimentBreakdownSchema = z.object({
  positive: z.number(),
  neutral: z.number(),
  negative: z.number(),
  unscored: z.number(),
})
export type SentimentBreakdown = z.infer<typeof SentimentBreakdownSchema>

export const InsightsResponseSchema = z.object({
  days: z.number(),
  perModel: z.array(PerModelStatSchema),
  perPrompt: z.array(PerPromptStatSchema),
  competitors: z.array(TopCompetitorSchema),
  sentiment: SentimentBreakdownSchema,
})
export type InsightsResponse = z.infer<typeof InsightsResponseSchema>

/** GET /api/results/:brandId */
export const ResultCompetitorEntrySchema = z.object({
  name: z.string(),
  position: z.number().nullable(),
})
export type ResultCompetitorEntry = z.infer<typeof ResultCompetitorEntrySchema>

export const ResultItemSchema = z.object({
  id: z.string(),
  promptText: z.string(),
  model: ModelSchema,
  mentioned: z.boolean(),
  rankPosition: z.number().nullable(),
  sentiment: z.enum(['positive', 'neutral', 'negative']).nullable().optional(),
  contextSnippet: z.string().nullable(),
  competitorsSnippet: z.string().nullable(),
  competitors: z.array(ResultCompetitorEntrySchema),
  aiAnswer: z.string().nullable(),
  rawResponse: z.string().nullable(),
  checkedAt: z.number(),
  latencyMs: z.number().nullable().optional(),
  inputTokens: z.number().nullable().optional(),
  outputTokens: z.number().nullable().optional(),
  modelCostUsd: z.number().nullable().optional(),
})

export const ResultsResponseSchema = z.object({
  results: z.array(ResultItemSchema),
  pagination: z.object({
    page: z.number(),
    limit: z.number(),
    total: z.number(),
  }),
})
export type ResultsResponse = z.infer<typeof ResultsResponseSchema>

/** POST /api/alerts */
export const AlertRequestSchema = z.object({
  type: z.enum(ALERT_TYPES),
  destination: z.string().min(1),
  active: z.boolean().default(true),
})
export type AlertRequest = z.infer<typeof AlertRequestSchema>

export const AlertResponseSchema = z.object({
  id: z.string(),
  type: z.enum(ALERT_TYPES),
  destination: z.string(),
  active: z.boolean(),
})
export type AlertResponse = z.infer<typeof AlertResponseSchema>

/** Standard API error envelope */
export const ApiErrorSchema = z.object({
  ok: z.literal(false),
  error: z.string(),
})
export type ApiError = z.infer<typeof ApiErrorSchema>

/** Tier + credit info */
export const TierInfoSchema = z.object({
  tier: z.enum(TIERS),
  brandLimit: z.number(),
  creditsBalance: z.number(),
  monthlyCredits: z.number(),
  trialExpiresAt: z.number().nullable(),
})
export type TierInfo = z.infer<typeof TierInfoSchema>
